import { Checkbox } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';

import {
  asSpelled,
  CompactBar,
  CompactChipLine,
  CompactDivider,
  CompactFilters,
  CompactMenu,
  CompactTitle,
  DataTable,
  FilterChip,
  HelpPopover,
  PageHeader,
  ParticipantAvatar,
  SelectChip,
  SplitList,
  StatusChip,
  STICKY_TABLE_TOP,
  useScrolledPast,
  useWorkToolbar,
  WorkToolbar,
  type DataTableState,
  type ICompactChip,
  type IDataColumn,
  WorkControls,
  openRow,
  PaneControl,
  RowOpens,
  usePanePrefs,
  useOpenRecord
} from '@/design';
import { formatDateTime } from '@/domains/survey';
import type { IPage } from '../api/oversight-contracts';
import type {
  IAdminPersonRow,
  IAffiliation,
  IPeopleQuery,
  PeopleAccount,
  PeopleCard,
  PeopleDisclosures,
  PeopleRegistry,
  PeopleSortKey,
  PeopleTeamRouting,
  PeopleTeamRoutingSource,
  PeopleVerification
} from '../api/participant-contracts';
import { participantsApi } from '../api/participants-api';
import {
  LoadError,
  PastTheEnd,
  RadioFilter,
  pageOf,
  toggleRepeated,
  useAdminData,
  withUnknown
} from '../components/admin-page';
import { accountKindWords } from '../components/account-facts';
import { ListArea, usePaneRead } from '../components/list-area';
import {
  CardLink,
  PEOPLE_PATH,
  ParticipantLink,
  peopleSearchOf,
  personHubPath,
  recordPath,
  rosterCardPath,
  type ICardLinkState
} from '../components/participant-link';
import { RegistryNumberChip } from '../components/registry-number-chip';
import { ROW_NAME, RowNameClicks } from '../components/row-name-click';
import {
  RETIRED_FILTERS,
  RetiredFilterChips,
  retiredFilterChips
} from '../components/retired-filters';
import { SearchMatches } from '../components/search-matches';
import { SearchScopes } from '../components/search-scopes';
import { UnderReviewChip } from '../components/under-review-chip';
import { UnverifiedBadge } from '../components/unverified-badge';
import { refusalMessage } from '../refusal';
import {
  cycleCodesOf,
  filterNamed,
  instantOf,
  LEAD_SHARED,
  liveAccountFilter,
  optionLabel,
  PEOPLE_ACCOUNT_STATES,
  SEARCH_RULE,
  sortOf,
  TEAM_ROUTING_SOURCES,
  TEAM_ROUTINGS,
  useCatalogue,
  withUnknownOption
} from '../components/record-filters';
import { registryNumbers } from '../registries';
import { slotWords } from '../roster-words';
import { SortHeader } from './compliance/queue-headers';
import { useOwnColumns } from './lists/work-page/columns';
import { WorkPageHeaderActions, useReadAt } from './lists/work-page/header-actions';
import {
  WorkPager,
  dropPaging,
  readItemPages,
  usePerPage,
  useWorkPaging,
  windowOf
} from './lists/work-page/pager';
import { profilePlace } from './person-hub/household';
import {
  countsKeyOf,
  RecordSearch,
  ViewCards,
  viewCardItems,
  type IRecordCard
} from './record-lists/record-work-page';
import { ParamChip } from './sec-adv/firm-filters';

/**
 * Who we hold, one row per person — `GET /api/v1/admin/people` (SR-215, person hub §6), under
 * `responses.browse`, so staff and admin alike. A row is an **account with every person it acts
 * for**, or a person no account acts for, alone — **persons only** (record search D3): a team, a
 * firm or an org is never a row or one of a row's participants. They are Teams' and Firms' rows,
 * and a row names its own in **Teams & firms** (`affiliations`). The grouping, the row's person,
 * the ranking, every filter, order and count are survey-api's; this screen sorts, groups, filters
 * and counts nothing.
 *
 * **A segmented work page** (SR-514, `docs/superpowers/specs/2026-10-28-work-pages-design.md`):
 * view cards with counts and no signals — All people, With an account, Not registered (`card=`,
 * All no key; the read always names one, `all` by default). The counts are one read under the
 * search and the filters (`GET /admin/people/counts`), never the card, the sort or the window;
 * the header's number is All's. **On rosters, not registered** (`card=roster`, SR-518) lists the
 * roster rows alone, searched or not: it reads every live team's and firm's roster, so it is read
 * only while open — no counts read counts it, and its card says "—" until its list answers.
 *
 * **Searched, People also lists the unregistered** (the owner's Q3): with `q` set the read asks
 * `include=roster`, derived here and never taken from the address, and survey-api adds a **roster
 * row** for each member named on a team's or firm's roster whom no participant resolves to. It
 * opens that member's card (`/team/people/roster/:rosterKey`, `AdminRosterCardPage`), the list
 * beside it. Each row searched says why it matched (`SearchMatches`, survey-api's `matches`), the
 * words of `q` marked.
 *
 * **The search reads as typed**, a quarter-second after the last key, replacing the address — the
 * heavier roster read behind the same pause. The "?" states survey-api's matching rule; nothing
 * here judges `q`'s shape (CLAUDE.md's rules of the code).
 *
 * **The search is the URL** — `q`, `card`, `survey` (repeated), `cycle`, `verification`,
 * `registry`, `account`, `disclosures`, `teamRouting`, `teamRoutingSource`, `teamLeadShared`,
 * `createdSince`, `sort` and the window (`page`, `size`) — so leaving for a hub and coming back
 * restores it, a search can be pasted, and a row's links hand it to the hub for its way back. Per
 * page, the columns and the card size are the viewer's, on this browser. **A value the URL names
 * that this build has no control for is still sent**, and shown in its control as spelled:
 * survey-api's 400 `invalid-query` names every problem at once, and those problems are what the
 * page says — `refusalMessage`, never the title alone. `createdSince` is a day in the URL and
 * that day's UTC midnight on the wire. `sort` is `key` or `key:desc`, as survey-api spells it.
 *
 * **The three team filters are Teams'** (the plan's D9), drawn here only while the address names
 * one — an old link stays readable and clearable, and survey-api still reads them over a row's
 * affiliations. "Lead CRD" is a radio pair behind its chip: survey-api binds `teamLeadShared` as a
 * `bool`, so a spelling the URL names that is not `true` gets a position of its own and its
 * binder's 400, said as sent.
 *
 * **People offers a person's filters alone** (SR-401's AC1; the owner's ruling of 2026-10-15):
 * Survey and Cycle, and the order in the headers. Numbers, BrokerCheck, Account, Disclosures and
 * Created are retired and drawn only while the URL names one (`RetiredFilterChips`). survey-api
 * still reads each over the row. The survey boxes and the cycle options come from the catalogue,
 * the cycles narrowed by the surveys ticked; losing it leaves the list usable with nothing to pick.
 *
 * **A result is drawn only under the query it answers**: the last one stays, dimmed, while the
 * next is out; a failed read over a loaded page says "Could not refresh", a first read that fails
 * says so in the table. An empty page says which empty it is — narrowed, unnarrowed, or past the
 * end. The old search's address redirects here (`AdminParticipantsListRedirect`). A hub opened
 * from a row is drawn beside the list (`AdminPeopleArea`), which the pane re-reads under the same
 * query and window (`peopleQueryOf`).
 */
export function AdminPeoplePage() {
  const location = useLocation();
  // Beside the list as *Opens in* says — its column, or a sheet over this page (SR-530).
  const openRecord = useOpenRecord();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const card = params.get('card');
  const surveys = params.getAll('survey');
  const cycle = params.get('cycle');
  const teamRouting = params.get('teamRouting');
  const teamRoutingSource = params.get('teamRoutingSource');
  const teamLeadShared = params.get('teamLeadShared');
  const sort = params.get('sort');

  const catalogue = useCatalogue();

  const paging = useWorkPaging();
  const { page, perPage, pages } = paging;
  const query = peopleQueryOf(params, page, perPage);
  const asked = JSON.stringify([query, pages]);
  const filtered = FILTERS.some((name) => filterNamed(params, name));
  const { data, error, reload, reading } = useAdminData<IAnswered>(
    async (signal) => {
      try {
        return {
          asked,
          q,
          searched: q !== '',
          filtered,
          card,
          page: await readItemPages(
            (at, pageSize) =>
              participantsApi.listPeople(peopleQueryOf(params, at, pageSize), signal),
            page,
            perPage,
            pages,
            PEOPLE_MAX_PAGE
          )
        };
      } catch (listError) {
        // survey-api's `problems[]` say which parameter is wrong and why; its title does not.
        throw new Error(refusalMessage(listError, 'Could not load this page'));
      }
    },
    [asked]
  );
  // The last answer while the next is out; nothing once this query's read has failed. An error
  // stands only while no newer read is out.
  const failed = error !== null && !reading;
  const shown = failed && data?.asked !== asked ? null : data;
  const readAt = useReadAt(data);
  // This query's total, never the last one's while the next is out.
  const listTotal = data?.asked === asked && !failed ? data.page.total : null;

  const counts = useAdminData(
    (signal) => participantsApi.peopleCounts(query, signal),
    [countsKeyOf(params)]
  );
  // A count survey-api did not just say is not drawn: a failed read leaves "…".
  const counted = counts.error ? null : counts.data;

  /** A change of search or filter starts at the top — made on the address as it is when it
   *  lands, so the search's delayed write never undoes a filter set meanwhile. */
  const update = (mutate: (next: URLSearchParams) => void, replace = false) =>
    setParams(
      (current) => {
        const next = dropPaging(new URLSearchParams(current));
        mutate(next);
        return next;
      },
      { replace }
    );
  // Sending the query already on screen asks again: a person registered a moment ago is found.
  const search = (text: string) => {
    if (text === q) refresh.onClick();
    else update((next) => (text === '' ? next.delete('q') : next.set('q', text)), true);
  };
  /** One parameter set to a value, or dropped for none / an empty value. */
  const choose = (name: string) => (value: Nullable<string>) =>
    update((next) => (value === null || value === '' ? next.delete(name) : next.set(name, value)));

  const surveyCodes = (catalogue?.surveys ?? []).map((survey) => survey.code);
  const toggleSurvey = (code: string, on: boolean) =>
    update((next) => toggleRepeated(next, 'survey', surveyCodes, code, on));

  // Cycle codes repeat across surveys, so the picker narrows with the surveys ticked above it.
  const cycleCodes = React.useMemo(
    () => cycleCodesOf(catalogue, surveys),
    [catalogue, surveys.join('\n')]
  );

  const sortKey = sort === null ? null : sort.replace(/:desc$/, '');
  const descending = sort?.endsWith(':desc') ?? false;
  // A press on the order in force turns it round; on another column, sorts by it its first way.
  const sortBy = (key: PeopleSortKey, first: 'asc' | 'desc') =>
    update((next) => {
      const dir = sortKey === key ? (descending ? 'asc' : 'desc') : first;
      next.set('sort', dir === 'desc' ? `${key}:desc` : key);
    });

  // The card and the order are where the reader is: Clear all keeps them.
  const clearAll = () =>
    setParams((current) => {
      const next = new URLSearchParams();
      for (const name of ['card', 'sort']) {
        const value = current.get(name);
        if (value !== null) next.set(name, value);
      }
      return next;
    });
  const leadSharedLabel = optionLabel(LEAD_SHARED, teamLeadShared);

  // A person beside the list (the row's first icon), or by *Opens in* (a press on the row).
  const prefs = usePanePrefs();
  const beside = (person: IAdminPersonRow) =>
    openRecord(personPath(person), { search: location.search, list: true } satisfies ICardLinkState);
  const open = (person: IAdminPersonRow) =>
    openRow(prefs, personPath(person), () => beside(person));

  // The team filters are Teams': drawn here only while the address names one, so an old link
  // stays readable and clearable — survey-api still reads them, over the rows' affiliations.
  const teamFilters = TEAM_FILTERS.some((name) => params.has(name));

  // The roster is read while its card is open alone: its count is its list's total, "—" else.
  const onRoster = card === 'roster';
  const rosterSaid = onRoster && listTotal !== null ? listTotal.toLocaleString('en-US') : '—';
  const cardLabel =
    shown?.card && shown.card !== 'all'
      ? (PEOPLE_CARDS.find((entry) => entry.key === shown.card)?.label ?? asSpelled(shown.card))
      : null;
  const tableState: DataTableState | undefined =
    shown === null
      ? failed
        ? { kind: 'error', content: <LoadError message={error} /> }
        : { kind: 'loading' }
      : shown.page.items.length === 0
        ? {
            kind: 'empty',
            content:
              shown.page.total > 0 ? (
                <PastTheEnd
                  page={shown.page.page}
                  pageSize={shown.page.pageSize}
                  total={shown.page.total}
                  counted={['person', 'people']}
                  onPage={paging.setPage}
                />
              ) : (
                emptyStatement(shown.searched, shown.filtered, cardLabel)
              )
          }
        : undefined;

  const own = personColumns(location.search, shown?.q ?? '').map((column) => {
    const sorted = SORTS[column.id];
    if (!sorted) return column;
    const label = typeof column.header === 'string' ? column.header : column.id;
    const active = sortKey === sorted.key;
    const direction = active ? (descending ? 'desc' : 'asc') : sorted.first;
    return {
      ...column,
      label,
      header: (
        <SortHeader
          label={label}
          direction={direction}
          active={active}
          note={`${active ? 'Sorted' : 'Sort'} ${orderWords(sorted.key, direction)}`}
          onPress={() => sortBy(sorted.key, sorted.first)}
        />
      )
    };
  });
  const chosen = useOwnColumns<IAdminPersonRow>('people');
  const columnsButton = chosen.button(own);

  // The header's number is All's: survey-api's count under the search and the filters — unread,
  // the list's own total where it is All.
  const total = counted?.cards.all?.count ?? ((card ?? 'all') === 'all' ? listTotal : null);
  const cards = <ViewCards cards={PEOPLE_CARDS} counted={counted} said={{ roster: rosterSaid }} />;

  const cardItems = viewCardItems(PEOPLE_CARDS, counted, params, { roster: rosterSaid });
  const openItem = cardItems.find((item) => item.current);

  // Every filter applied as a chip with its value — under the toolbar and on the compact bar's
  // second row; a press opens its control in the toolbar (`open`, by its name), the ✕ clears it.
  const toolbar = useWorkToolbar();
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const openFilters = () => {
    setFiltersOpen(true);
    return true as const;
  };
  const appliedChipsWith = (open: (label: string) => true): ICompactChip[] => {
    const said: [string, string, Nullable<string>, () => void][] = [
      [
        'survey',
        'Survey',
        surveys.length > 0 ? surveys.map(asSpelled).join(', ') : null,
        () => update((next) => next.delete('survey'))
      ],
      ['cycle', 'Cycle', cycle === null ? null : asSpelled(cycle), () => choose('cycle')(null)],
      [
        'teamRouting',
        'Team routing',
        optionLabel(TEAM_ROUTINGS, teamRouting),
        () => choose('teamRouting')(null)
      ],
      [
        'teamRoutingSource',
        'Routing decided by',
        optionLabel(TEAM_ROUTING_SOURCES, teamRoutingSource),
        () => choose('teamRoutingSource')(null)
      ],
      ['teamLeadShared', 'Lead CRD', leadSharedLabel, () => choose('teamLeadShared')(null)]
    ];
    return [
      ...said.flatMap(([key, label, value, onRemove]) =>
        value === null
          ? []
          : [
              {
                key,
                chip: (
                  <ParamChip
                    words={`${label}: ${value}`}
                    body={null}
                    onPress={() => open(label)}
                    onRemove={onRemove}
                  />
                )
              }
            ]
      ),
      ...retiredFilterChips(params, choose, PEOPLE_ACCOUNT_STATES, open)
    ];
  };
  const appliedChips = appliedChipsWith(openFilters);
  const pageChips = appliedChipsWith(toolbar.openQuick);

  // Where the page's own controls end: scrolled under the app bar, the compact bar stands for them.
  const [controlsEnd, setControlsEnd] = React.useState<Nullable<HTMLElement>>(null);
  const scrolled = useScrolledPast(controlsEnd);

  // The controls drawn twice — in the page, and slimmed in the compact bar once they scroll away.
  const refresh = {
    reading: reading || counts.reading,
    readAt,
    onClick: () => {
      void reload();
      void counts.reload();
    }
  };
  const help = (
    <HelpPopover label="About People" align="end">
      <p>
        Every person we hold. Searched, it also finds the people named on a team’s or firm’s roster
        who have not registered. Teams and firms are their own lists.
      </p>
      <ul className="grid list-disc gap-1 pl-5">
        {PEOPLE_CARDS.map((entry) => (
          <li key={entry.key}>
            <span className="font-medium">{entry.label}</span> — {entry.about}
          </li>
        ))}
      </ul>
      <p>
        On rosters, not registered reads every team’s and firm’s roster, so it opens slower; it is
        counted once open.
      </p>
      <p>The search reads as you type. {SEARCH_RULE}</p>
      <p>
        A row opens beside the list by its first icon and in a new tab by ↗; a click on the row or
        its name goes where «Opens in» says.
      </p>
      <p>
        Person, Created and Last sign-in sort the list, a press again the other way; with none
        chosen a search ranks the best match first, else the newest come first. Clear all keeps
        the card and the order.
      </p>
    </HelpPopover>
  );
  const searchLabel = 'Filter people';
  // The toolbar's quick filters, drawn in the page and again in the compact bar.
  const quickFilters = (
    <>
      {(surveyCodes.length > 0 || surveys.length > 0) && (
        <FilterChip label="Survey" summary={surveys.map(asSpelled).join(', ')}>
          <fieldset aria-label="Survey" className="flex flex-wrap items-center gap-4">
            {withUnknown(surveyCodes, surveys).map((code) => (
              <label key={code} className="flex items-center gap-2">
                <Checkbox
                  checked={surveys.includes(code)}
                  onCheckedChange={(next) => toggleSurvey(code, next === true)}
                />
                {asSpelled(code)}
              </label>
            ))}
          </fieldset>
        </FilterChip>
      )}
      <SelectChip
        label="Cycle"
        any="Every cycle"
        value={cycle}
        options={cycleCodes.map((code) => ({ value: code, label: code }))}
        onChoose={choose('cycle')}
      />
      {teamFilters && (
        <>
          <SelectChip
            label="Team routing"
            any="Any routing"
            value={teamRouting}
            options={TEAM_ROUTINGS}
            onChoose={choose('teamRouting')}
          />
          <SelectChip
            label="Routing decided by"
            any="Anyone"
            value={teamRoutingSource}
            options={TEAM_ROUTING_SOURCES}
            onChoose={choose('teamRoutingSource')}
          />
          <FilterChip label="Lead CRD" summary={leadSharedLabel}>
            <RadioFilter
              label="Lead CRD"
              name="people-team-lead-shared"
              options={withUnknownOption(LEAD_SHARED, teamLeadShared)}
              value={teamLeadShared}
              onChoose={choose('teamLeadShared')}
            />
          </FilterChip>
        </>
      )}
      <RetiredFilterChips
        params={params}
        choose={choose}
        accountStates={PEOPLE_ACCOUNT_STATES}
        name="people"
      />
    </>
  );

  return (
    <>
      <PageHeader
        title="People"
        row
        actions={
          <WorkPageHeaderActions
            summary={
              total === null
                ? null
                : `${total.toLocaleString('en-US')} ${total === 1 ? 'person' : 'people'}`
            }
            summaryData={{ 'data-records-summary': '' }}
            refresh={refresh}
            help={help}
          />
        }
      />
      <CompactBar
        shown={scrolled}
        label="People, compact"
        actions={<WorkPageHeaderActions summary={null} refresh={refresh} help={help} />}
        below={
          appliedChips.length > 0 && <CompactChipLine chips={appliedChips} onClearAll={clearAll} />
        }
      >
        <CompactTitle>People</CompactTitle>
        {openItem && (
          <CompactMenu
            label="Show"
            chip
            trigger={
              <>
                {openItem.label}
                <span className="font-semibold tabular-nums">{openItem.count}</span>
              </>
            }
            items={cardItems}
          />
        )}
        <CompactDivider />
        <RecordSearch
          q={q}
          label={searchLabel}
          placeholder={SEARCH_PLACEHOLDER}
          onSearch={search}
          compact
        />
        <CompactFilters count={appliedChips.length} open={filtersOpen} onOpenChange={setFiltersOpen}>
          {quickFilters}
        </CompactFilters>
        <span className="flex-1" />
        <CompactDivider />
        {columnsButton}
        <CompactDivider />
      </CompactBar>
      <div className="grid min-w-0 gap-3 px-6 py-4">
        {cards}

        <WorkControls end={setControlsEnd}>
          <WorkToolbar
            toolbar={toolbar}
            status={{
              reading,
              count: shown ? `${shown.page.total.toLocaleString('en-US')} found` : undefined
            }}
            search={
              <RecordSearch
                q={q}
                label={searchLabel}
                placeholder={SEARCH_PLACEHOLDER}
                onSearch={search}
              />
            }
            quick={quickFilters}
            quickCount={appliedChips.length}
            end={
              <>
                {columnsButton}
                <PaneControl prefs={prefs} kind="record" />
              </>
            }
          />
          {pageChips.length > 0 && (
            <CompactChipLine label="Filters" chips={pageChips} onClearAll={clearAll} />
          )}
          <SearchScopes
            scope="people"
            q={q}
            total={data?.asked === asked ? data.page.total : null}
            failed={failed && data?.asked !== asked}
          />
        </WorkControls>

        {failed && shown !== null && (
          <LoadError message={`Could not refresh — showing the list as last read. ${error}`} />
        )}
        {onRoster && reading && (
          <p role="status" className="text-sm text-muted-foreground">
            Reading rosters…
          </p>
        )}

        <RowNameClicks>
          <DataTable
            label="People"
            columns={[openColumn(beside), ...chosen.resolve(own)]}
            rows={tableState ? null : (shown?.page.items ?? null)}
            rowKey={(person) => person.key}
            // A row's hooks: its key and what it is — a spec finds a roster row, which has no
            // participant, by them.
            rowProps={(person) =>
              ({
                'data-key': person.key,
                'data-row-type': person.rowType
              }) as React.HTMLAttributes<HTMLTableRowElement>
            }
            onOpen={open}
            state={tableState}
            busy={reading && shown !== null}
            // Wide enough that a phone scrolls the frame rather than crushing an address into a
            // column of five-letter lines.
            minWidth="60rem"
            stickyTop={STICKY_TABLE_TOP}
          />
        </RowNameClicks>

        {shown && <WorkPager {...paging.pager} total={shown.page.total} busy={reading} sticky />}
      </div>
    </>
  );
}

/** survey-api's page ceiling on People. */
const PEOPLE_MAX_PAGE = 100;

const SEARCH_PLACEHOLDER = 'Filter people…';

/** People's view cards — `card=`, All no key. */
const PEOPLE_CARDS: readonly IRecordCard[] = [
  {
    key: 'all',
    label: 'All people',
    hint: 'Every row',
    about: 'every person we hold and, searched, the roster members who have not registered.'
  },
  {
    key: 'account',
    label: 'With an account',
    hint: 'Rows an account is',
    about: 'the rows an account is — the account and every person it acts for.'
  },
  {
    key: 'unregistered',
    label: 'Not registered',
    hint: 'No account acts for them',
    about:
      'the persons no account acts for and, searched, the roster members who have not registered.'
  },
  {
    key: 'roster',
    label: 'On rosters, not registered',
    hint: 'Named on a roster, never registered',
    about: 'the members named on a live team’s or firm’s roster whom no record resolves to.'
  }
];

/** The columns whose header sorts, by id: survey-api's key and its first order. */
const SORTS: Readonly<Record<string, { key: PeopleSortKey; first: 'asc' | 'desc' }>> = {
  person: { key: 'name', first: 'asc' },
  created: { key: 'created', first: 'desc' },
  'last-sign-in': { key: 'last_login', first: 'desc' }
};

/** A sort key's order in words. */
function orderWords(key: PeopleSortKey, dir: 'asc' | 'desc'): string {
  if (key === 'name') return dir === 'asc' ? 'A to Z' : 'Z to A';
  return dir === 'desc' ? 'newest first' : 'oldest first';
}

/**
 * The URL's search as the query survey-api is asked — `q`, `card` (`all` where the URL names none),
 * `survey` (repeated), `cycle`, `verification`, `registry`, `account`, `disclosures`, the three
 * team filters, `createdSince`, `sort`, the page and its size, each sent as spelled.
 * One function for the list and its pane beside a hub, so the two are one query.
 */
export function peopleQueryOf(
  params: URLSearchParams,
  page = pageOf(params.get('page')),
  pageSize?: number
): IPeopleQuery {
  const createdSince = params.get('createdSince');
  const sort = params.get('sort');
  const q = params.get('q') ?? '';
  return {
    q,
    // All, the URL's clean default, is sent by name.
    card: (params.get('card') ?? 'all') as PeopleCard,
    survey: params.getAll('survey'),
    cycle: params.get('cycle') ?? undefined,
    // Sent as the URL spells them: an unknown value is survey-api's to refuse, by name.
    verification: (params.get('verification') ?? undefined) as PeopleVerification | undefined,
    registry: (params.get('registry') ?? undefined) as PeopleRegistry | undefined,
    account: (liveAccountFilter(params.get('account')) ?? undefined) as PeopleAccount | undefined,
    disclosures: (params.get('disclosures') ?? undefined) as PeopleDisclosures | undefined,
    teamRouting: (params.get('teamRouting') ?? undefined) as PeopleTeamRouting | undefined,
    teamRoutingSource: (params.get('teamRoutingSource') ?? undefined) as
      | PeopleTeamRoutingSource
      | undefined,
    // `true` narrows; any other spelling goes as it is, for survey-api's binder to refuse.
    teamLeadShared: (params.get('teamLeadShared') ?? undefined) as unknown as boolean | undefined,
    createdSince: createdSince === null ? undefined : instantOf(createdSince),
    // Derived, never read from the address (the owner's Q3): a searched People also lists the
    // members named on a roster who have not registered.
    include: q === '' ? undefined : 'roster',
    sort: sort === null ? undefined : sortOf<PeopleSortKey>(sort),
    page,
    pageSize
  };
}

/** People and the hubs, as one area: the list, or a hub — or a roster card (SR-414) — with the
 *  list beside it. */
export function AdminPeopleArea() {
  return (
    <ListArea
      param={['participantId', 'rosterKey']}
      list={PEOPLE_PATH}
      paneTitle="People"
      pane={(search, selected) => <PeoplePane search={search} selected={selected} />}
    />
  );
}

/**
 * The People list as the pane beside a hub draws it — the search the hub was opened from, in the
 * list's own Per page and window, one person a line, their place under the name and their first
 * registry number at the right. The person whose household holds the open hub is the current one.
 */
function PeoplePane({ search, selected }: { search: string; selected: string }) {
  const [perPage] = usePerPage();
  const { shown, pager, status } = usePaneRead(
    search,
    'Nobody on this list.',
    (params, page, signal) => {
      const window = windowOf(params, perPage);
      return readItemPages(
        (at, pageSize) => participantsApi.listPeople(peopleQueryOf(params, at, pageSize), signal),
        page,
        perPage,
        page === window.page ? window.pages : 1,
        PEOPLE_MAX_PAGE
      );
    }
  );
  // The open record is a row's own (a roster card's key) or one of its persons.
  const current = shown?.items.find(
    (person) =>
      person.key === selected ||
      person.participants.some((participant) => participant.participantId === selected)
  );
  return (
    <SplitList
      label="People"
      selected={current ? current.key : null}
      items={
        shown?.items.map((person) => {
          const numbers = person.participants.flatMap((participant) =>
            registryNumbers(participant.identifiers)
          );
          return {
            key: person.key,
            to: personPath(person),
            state: { search, list: true } satisfies ICardLinkState,
            primary: person.displayName,
            secondary:
              person.rowType === 'roster'
                ? 'Not registered'
                : (profilePlace(person.profile) ?? person.email ?? 'No account'),
            trailing: numbers[0] ? <RegistryNumberChip number={numbers[0]} compact /> : undefined
          };
        }) ?? null
      }
      status={status}
      footer={pager}
    />
  );
}

/** Where a row opens: its person's page, or a roster row's card (SR-414). */
function personPath(person: IAdminPersonRow): string {
  return person.rowType === 'roster'
    ? rosterCardPath(person.key)
    : personHubPath(person.participantId);
}

/** The ways in first on a row: beside the list, and ↗ its page in a new tab, the list kept in this
 *  one; the column is none the viewer arranges or hides. */
function openColumn(beside: (person: IAdminPersonRow) => void): IDataColumn<IAdminPersonRow> {
  return {
    id: 'open',
    header: <span className="sr-only">Open</span>,
    label: 'Open',
    className: 'w-0 whitespace-nowrap',
    cell: (person) => (
      <RowOpens
        name={person.displayName}
        onPane={() => beside(person)}
        to={personPath(person)}
        tabWords={`Open ${person.displayName} in a new tab`}
      />
    )
  };
}

/** Each person's name on a row of several, to say whose value a match is (the plan's D17). */
function namesOf(person: IAdminPersonRow): ReadonlyMap<string, string> {
  return new Map(
    person.participants.map((participant) => [participant.participantId, participant.displayName])
  );
}

/** The list's columns — three a person is and their teams and firms, then three more the picker
 *  can take away. `q` is the search the rows answer, whose words their matches mark. */
function personColumns(search: string, q: string): IDataColumn<IAdminPersonRow>[] {
  return [
    {
      id: 'person',
      header: 'Person',
      cell: (person) => {
        const place = profilePlace(person.profile);
        return (
          <>
            {person.rowType === 'roster' ? (
              <>
                <span className="flex items-start justify-between gap-1" {...ROW_NAME}>
                  <CardLink
                    to={rosterCardPath(person.key)}
                    search={search}
                    fromList
                    className="min-w-0 break-words"
                  >
                    {person.displayName}
                  </CardLink>
                </span>
                <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
                  <StatusChip>Not registered</StatusChip>
                  {person.roster.crd !== null && (
                    <span className="text-xs text-muted-foreground">CRD {person.roster.crd}</span>
                  )}
                </span>
              </>
            ) : (
              <span className="flex items-start justify-between gap-1" {...ROW_NAME}>
                <ParticipantLink
                  participant={person}
                  search={search}
                  fromList
                  className="min-w-0 break-words"
                />
              </span>
            )}
            {place && <p className="mt-0.5 text-xs text-muted-foreground">{place}</p>}
            <SearchMatches matches={person.matches ?? []} q={q} names={namesOf(person)} />
          </>
        );
      }
    },
    {
      id: 'account',
      header: 'Account',
      cell: (person) =>
        person.rowType === 'roster' ? (
          <>
            <span className="text-muted-foreground">Not registered</span>
            {person.roster.emails.length > 0 && (
              <ul aria-label="Emails on the roster" className="mt-0.5 grid text-xs">
                {person.roster.emails.map((email) => (
                  <li key={email} className="break-all">
                    {email}
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : person.email === null ? (
          <span className="text-muted-foreground">No account</span>
        ) : (
          <>
            <span className="break-all">{person.email}</span>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {[person.kind === null ? null : accountKindWords(person.kind), person.status]
                .filter((part) => part !== null)
                .join(' · ')}
            </p>
          </>
        )
    },
    {
      id: 'participants',
      header: 'Participants',
      cell: (person) =>
        person.rowType === 'roster' ? (
          <span className="text-muted-foreground">No record of their own</span>
        ) : (
          <PersonParticipants person={person} search={search} />
        )
    },
    {
      id: 'affiliations',
      header: 'Teams & firms',
      optional: true,
      cell: (person) => <Affiliations affiliations={person.affiliations ?? []} />
    },
    {
      id: 'surveys',
      header: 'Surveys',
      optional: true,
      cell: (person) =>
        person.surveys.length === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {person.surveys.map((code) => (
              <StatusChip key={code}>{code}</StatusChip>
            ))}
          </span>
        )
    },
    {
      id: 'created',
      header: 'Created',
      optional: true,
      className: 'text-xs whitespace-nowrap text-muted-foreground',
      cell: (person) => formatDateTime(person.createdAt)
    },
    {
      id: 'last-sign-in',
      header: 'Last sign-in',
      optional: true,
      className: 'text-xs whitespace-nowrap text-muted-foreground',
      cell: lastSignIn
    }
  ];
}

/**
 * The participant search's address, `/admin/participants`, which bookmarks and pasted searches
 * made before SR-215 point at: it lands on People with its query translated (`peopleSearchOf`).
 */
export function AdminParticipantsListRedirect() {
  const { search } = useLocation();
  return <Navigate to={`${PEOPLE_PATH}${peopleSearchOf(search)}`} replace />;
}

/**
 * One person's participants — each a chip leading to the hub opened on that participant, with its
 * registry numbers; every link carries the search for the hub's way back. The person's own name,
 * in the first column, is the link to their hub: a link as well as the row's Enter and click, so
 * it can be opened in another tab.
 */
function PersonParticipants({ person, search }: { person: IAdminPersonRow; search: string }) {
  return (
    <ul aria-label="Participants" className="grid gap-1.5">
      {person.participants.map((participant) => {
        // A participant's numbers a registry answers for are drawn as chips; an internal id
        // is not "who this is".
        const numbers = registryNumbers(participant.identifiers);
        return (
          <li key={participant.participantId} className="flex flex-wrap items-center gap-1.5">
            <CardLink
              to={recordPath(participant.participantId, participant)}
              search={search}
              fromList
              className="inline-flex items-center gap-1.5"
            >
              <ParticipantAvatar
                photoUri={participant.photoUri}
                displayName={participant.displayName}
                size="sm"
                decorative
              />
              <StatusChip>{participant.kind}</StatusChip>
              <span className="break-words">{participant.displayName}</span>
            </CardLink>
            {numbers.map((identifier) => (
              <RegistryNumberChip
                key={`${identifier.idType} ${identifier.value}`}
                number={identifier}
              />
            ))}
            {participant.denied && <StatusChip tone="danger">Denied</StatusChip>}
            <UnderReviewChip underReview={participant.underReview} />
            {/* Each registry number's chip already says it is unverified; the badge speaks
                for a participant with none to show. */}
            {participant.unverified && numbers.length === 0 && <UnverifiedBadge />}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * A row's teams and firms (SR-414, record search §3.7; the plan's D11) — each linked to its record
 * under its own list, how it reaches the person said beside it: a roster slot in words, and
 * "through their login" where an account of the person acts for it. In the order sent.
 */
function Affiliations({ affiliations }: { affiliations: readonly IAffiliation[] }) {
  if (affiliations.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <ul aria-label="Teams & firms" className="grid gap-1">
      {affiliations.map((affiliation) => {
        const how = [
          affiliation.slot !== null && affiliation.role !== null
            ? slotWords({ role: affiliation.role, slot: affiliation.slot })
            : null,
          affiliation.viaAccount ? 'through their login' : null
        ].filter((part) => part !== null);
        return (
          <li key={affiliation.participantId}>
            <ParticipantLink participant={affiliation} className="break-words" />
            {how.length > 0 && (
              <span className="text-xs text-muted-foreground"> · {how.join(' · ')}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** When the row's account last signed in — "Never" for an account that has not, and a dash for a
 *  row with no account to sign in with. */
function lastSignIn(person: IAdminPersonRow): string {
  if (person.lastLoginAt !== null) return formatDateTime(person.lastLoginAt);
  return person.accountId === null ? '—' : 'Never';
}

/** A page of results, with the query it answers and whether anything narrowed that query. */
interface IAnswered {
  asked: string;
  /** The search the page answers — whose words the rows' matches mark. */
  q: string;
  searched: boolean;
  filtered: boolean;
  card: Nullable<string>;
  page: IPage<IAdminPersonRow>;
}

/** The URL's filters, bar `q` and the order: what makes an empty result "narrowed". */
const FILTERS = [
  'survey',
  'cycle',
  'teamRouting',
  'teamRoutingSource',
  'teamLeadShared',
  ...RETIRED_FILTERS
] as const;

/** The three team filters — Teams' own since SR-414, drawn here only while the URL names one. */
const TEAM_FILTERS = ['teamRouting', 'teamRoutingSource', 'teamLeadShared'] as const;

/** Says what narrowed the list, so "nobody matches" is never read as "nobody is held". */
function emptyStatement(searched: boolean, filtered: boolean, card: Nullable<string>): string {
  const Nobody = card === null ? 'Nobody' : `Nobody in ${card}`;
  if (searched && filtered) return `${Nobody} matches this search with these filters.`;
  if (searched) return `${Nobody} matches this search.`;
  if (filtered) return `${Nobody} matches these filters.`;
  return card === null ? 'Nobody is held yet.' : `${Nobody}.`;
}
